
import React, { useEffect, useMemo, useState } from 'react'
import { FormGroup, FormLabel } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import Swal from 'sweetalert2'

import { useProgrammation } from '@/Hooks/UseProgrammation'
import useTranslation from '@/Hooks/UseTranslation'

import { deleteRelation, updateRelation } from '@/Services/ProgrammationManagerService'
import { removeRelationAction, updateRelationsAction } from '@/Redux/Actions/ProgrammationManagerActions'

import Modal from '@/Components/Modal/Modal'
import InputSelect from '../../components/InputSelect'

import Spinner from '@/Components/SvgImages/Spinner/Spinner'
import TrashIcon from '@/Components/SvgImages/TrashIcon'
import SwapIcon from '@/Components/SvgImages/Swap/Swap'

import './RelationEditor.scss'
import { getItemsOptionsFromTaskList, getWorksOptionsFromTaskList } from './RelationsHelper'
import InputText from '../../components/InputText'
import RelationTypeSelector from './RelationTypeSelector'

const DEFAULT_RELATION_TYPE = 'start_to_start'

interface Props {
  relation: any
  onClose: () => void
}

const RelationEditor = ({ relation, onClose }: Props) => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { tasks } = useSelector((state: any) => state.ProgrammationReducer)
  const macroResult = useSelector((state: any) => state.MacroReducer.result)

  const { itemOpened, patchItemOpened } = useProgrammation()

  const [ fromTask, setFromTask ] = useState<any>()
  const [ toTask, setToTask ] = useState<any>()
  const [ type, setType ] = useState<any>(DEFAULT_RELATION_TYPE)
  const [ lag, setLag ] = useState('0')
  const [ saving, setSaving ] = useState(false)
  const [ deleting, setDeleting ] = useState(false)

  const relationsTypes = [
    {
      value: 'end_to_start',
      label: t('RELATIONS_CREATOR:Preset:end_to_start'),
      description: t('RELATIONS_CREATOR:PresetDescription:end_to_start'),
      code: t('RELATIONS_CREATOR:Type:end_to_start'),
    },
    {
      value: 'start_to_start',
      label: t('RELATIONS_CREATOR:Preset:start_to_start'),
      description: t('RELATIONS_CREATOR:PresetDescription:start_to_start'),
      code: t('RELATIONS_CREATOR:Type:start_to_start'),
    },
    {
      value: 'end_to_end',
      label: t('RELATIONS_CREATOR:Preset:end_to_end'),
      description: t('RELATIONS_CREATOR:PresetDescription:end_to_end'),
      code: t('RELATIONS_CREATOR:Type:end_to_end'),
    },
    {
      value: 'start_to_end',
      label: t('RELATIONS_CREATOR:Preset:start_to_end'),
      description: t('RELATIONS_CREATOR:PresetDescription:start_to_end'),
      code: t('RELATIONS_CREATOR:Type:start_to_end'),
    },
  ]

  const tasksOptions = useMemo(() => {

    if (itemOpened.type !== 'trabajo') {

      return getItemsOptionsFromTaskList({
        list: macroResult,
        currentItem: itemOpened,
        currentLabel: t('RELATIONS_CREATOR:CurrentItem'),
        noNameText: t('RELATIONS_CREATOR:Nameless'),
        level: 0,
      })
    } else if (itemOpened.type === 'trabajo') {

      return getWorksOptionsFromTaskList({
        list: macroResult,
        currentWork: itemOpened,
        currentLabel: t('RELATIONS_CREATOR:CurrentWork'),
        noNameText: t('RELATIONS_CREATOR:Nameless'),
        level: 0,
      })
    }
  }, [macroResult, itemOpened])

  const validateRelation = (relation: any) => {

    const isValid = true

    const taskFromIsCurrentItem = relation.from_task === itemOpened._id
    const taskToIsCurrentItem = relation.to_task === itemOpened._id

    if (!taskFromIsCurrentItem && !taskToIsCurrentItem) {

      Swal.fire({
        title: t('RELATION_ITEM:TaskNotSelectedTitle'),
        text: t('RELATION_ITEM:TaskNotSelectedContent'),
        icon: 'warning',
        confirmButtonText: t('RELATION_ITEM:OkButton'),
        width: '500px',
      })
      return false
    }

    return isValid
  }

  const handleSaveRelation = async (relation: any) => {

    if (!validateRelation(relation)) return false

    setSaving(true)

    console.log('relation', relation)

    const [ error, data ] = await updateRelation(relation._id, relation, itemOpened._id)

    setSaving(false)

    if (error) {
      console.log(error)
      return false
    }

    patchItemOpened({
      relations: itemOpened.relations.map((r: any) => r._id === relation._id ? relation : r)
    })

    dispatch(updateRelationsAction(data))

    return true
  }

  const handleChangeTaskFrom = async (task_id: string) => {

    const nextRelation = {
      _id: relation._id,
      type,
      from_task: task_id,
      to_task: toTask._id,
      lag: Number(lag) || 0,
    }

    const saved = await handleSaveRelation(nextRelation)

    if (saved) {
      setFromTask(tasks.find((task: any) => task._id === task_id))
    }
  }

  const handleChangeType = async (type: string) => {

    const nextRelation = {
      _id: relation._id,
      type,
      from_task: fromTask._id,
      to_task: toTask._id,
      lag: Number(lag) || 0,
    }

    const saved = await handleSaveRelation(nextRelation)

    if (saved) {
      setType(type)
    }
  }

  const handleChangeTaskTo = async (task_id: string) => {

    const nextRelation = {
      _id: relation._id,
      type,
      from_task: fromTask._id,
      to_task: task_id,
      lag: Number(lag) || 0,
    }

    const saved = await handleSaveRelation(nextRelation)

    if (saved) {
      setToTask(tasks.find((task: any) => task._id === task_id))
    }
  }

  const handleChangeLag = async (lagValue: string) => {

    const nextRelation = {
      _id: relation._id,
      type,
      from_task: fromTask._id,
      to_task: toTask._id,
      lag: Number(lagValue) || 0,
    }

    const saved = await handleSaveRelation(nextRelation)

    if (saved) {
      setLag(lagValue)
    }
  }

  const handleSwapTasks = async () => {

    const nextRelation = {
      _id: relation._id,
      type,
      from_task: toTask._id,
      to_task: fromTask._id,
      lag: Number(lag) || 0,
    }

    const saved = await handleSaveRelation(nextRelation)

    if (saved) {
      setFromTask(toTask)
      setToTask(fromTask)
    }
  }

  const adjustLag = (amount: number) => {

    handleChangeLag(`${(Number(lag) || 0) + amount}`)
  }

  const getRelationSummary = () => {

    const relationType = relationsTypes.find(({ value }) => value === type)
    const lagValue = Number(lag) || 0
    const lagText = lagValue === 0
      ? t('RELATIONS_CREATOR:SummaryNoLag')
      : t(lagValue > 0 ? 'RELATIONS_CREATOR:SummaryPositiveLag' : 'RELATIONS_CREATOR:SummaryNegativeLag')
          .replace('{{days}}', `${Math.abs(lagValue)}`)

    return t('RELATIONS_CREATOR:Summary')
      .replace('{{from}}', fromTask?.name || t('RELATIONS_CREATOR:UnselectedTask'))
      .replace('{{type}}', relationType?.code.toLowerCase() || '')
      .replace('{{to}}', toTask?.name || t('RELATIONS_CREATOR:UnselectedTask'))
      .replace('{{lag}}', lagText)
  }

  const handleDelete = async () => {

    setDeleting(true)

    const [ error, data ] = await deleteRelation(relation._id)

    setDeleting(false)

    if (error) {
      return console.log(error)
    }

    onClose()

    patchItemOpened({
      relations: itemOpened.relations.filter((r: any) => r._id !== relation._id)
    })

    dispatch(removeRelationAction({ relation_id: relation._id, ...data }))
  }

  useEffect(() => {

    const getTasks = () => {

      const fromTask = tasks.find((task: any) => task._id === relation.from_task)
      const toTask = tasks.find((task: any) => task._id === relation.to_task)

      setFromTask(fromTask)
      setToTask(toTask)
    }

    if (!relation) {
      setFromTask(undefined)
      setToTask(undefined)
      setType(DEFAULT_RELATION_TYPE)
      setLag('0')
      setSaving(false)
      setDeleting(false)
      return
    }

    setType(relation.type || DEFAULT_RELATION_TYPE)
    setLag(`${relation.lag || 0}`)
    getTasks()
  }, [ relation ])

  if (!relation || !fromTask || !toTask) return null

  return (
    <Modal
      show={!!relation}
      title={t('RELATION_EDITOR:Title')}
      onClose={onClose}
      optionsButtons={[{
        icon: (deleting
                ? <Spinner height={20} width={20} fill="#e21c1c" />
                : <TrashIcon height={20} width={20} color="#e21c1c" />
        ),
        onClick: () => handleDelete(),
        disabled: deleting
      }]}
      backdropClassName='relation-editor-backdrop'
      className='relation-editor'
      showCloseButton={true}
      scrollable
      zIndex='2'
    >
      {relation?.relation && (
        <>
          {t('RELATION_ITEM:RelationType')}
        </>
      )}
      {relation && !relation?.relation && (
        <>
          <div className="relation-task-pair">
            <FormGroup className="relation-task-field">
              <FormLabel>{t('RELATIONS_CREATOR:OriginTask')}</FormLabel>
              <InputSelect
                value={fromTask._id}
                options={tasksOptions}
                onChange={handleChangeTaskFrom}
                renderOption={(option: any) => (
                  <>
                    {option.isGroup && (
                      <optgroup key={option.value} label={option.label}></optgroup>
                    )}
                    {!option.isGroup && (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    )}
                  </>
                )}
                disabled={saving}
              />
            </FormGroup>
            <button
              type="button"
              className="relation-swap-button"
              onClick={handleSwapTasks}
              disabled={saving}
              title={t('RELATIONS_CREATOR:SwapTasks')}
              aria-label={t('RELATIONS_CREATOR:SwapTasks')}
            >
              <SwapIcon height={18} width={18} />
            </button>
            <FormGroup className="relation-task-field">
              <FormLabel>{t('RELATIONS_CREATOR:DestinationTask')}</FormLabel>
              <InputSelect
                value={toTask._id}
                options={tasksOptions}
                onChange={handleChangeTaskTo}
                renderOption={(option: any) => (
                  <>
                    {option.isGroup && (
                      <optgroup key={option.value} label={option.label}></optgroup>
                    )}
                    {!option.isGroup && (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    )}
                  </>
                )}
                disabled={saving}
              />
            </FormGroup>
          </div>
          <FormGroup>
            <FormLabel>{t('RELATIONS_CREATOR:Behavior')}</FormLabel>
            <RelationTypeSelector
              value={type}
              options={relationsTypes}
              originName={fromTask.name}
              destinationName={toTask.name}
              onChange={handleChangeType}
              disabled={saving}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>{t('RELATIONS_CREATOR:LagDays')}</FormLabel>
            <div className="relation-lag-control">
              <button type="button" onClick={() => adjustLag(-1)} disabled={saving} aria-label={t('RELATIONS_CREATOR:DecreaseLag')}>−</button>
              <InputText
                type="number"
                className="relation-lag-input"
                value={lag}
                onChange={handleChangeLag}
                delay={0}
                disabled={saving}
              />
              <button type="button" onClick={() => adjustLag(1)} disabled={saving} aria-label={t('RELATIONS_CREATOR:IncreaseLag')}>+</button>
              <span>{t('RELATIONS_CREATOR:WorkingDays')}</span>
            </div>
          </FormGroup>
          <div className="relation-summary" aria-live="polite">{getRelationSummary()}</div>
        </>
      )}
    </Modal>
  )
}

export default RelationEditor
